import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@material-ui/core';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData, postData } from '../../../apiService';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import './DailyFoodRegister.css';

const DailyFoodRegister = () => {
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);
    const [foodItems, setFoodItems] = useState([]);
    const [foodItemOptions, setFoodItemOptions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [applyToAll, setApplyToAll] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();
    const history = useHistory();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const today = new Date().toISOString().split('T')[0];
        setFromDate(today);
        setToDate(today);
        fetchCategories();
        fetchCustomerOptions();
    }, [isAuthenticated, token, logout]);

    const getCategoryStyle = (name) => {
        const nameLower = name.toLowerCase();

        // Emoji mapping based on category name
        const emojiMap = {
            'break fast': '🍳',
            'lunch': '🍱',
            'dinner': '🍽️',
            'snack': '🍪',
            'tea': '☕',
            'coffee': '☕',
            'dessert': '🍰',
            'beverage': '🥤'
        };

        // Color mapping based on category name
        const colorMap = {
            'break fast': '#FF6B6B',
            'lunch': '#4ECDC4',
            'dinner': '#7ba7e0ff',
            'snack': '#FFD93D',
            'tea': '#A8E6CF',
            'coffee': '#8B4513',
            'dessert': '#FFB6C1',
            'beverage': '#87CEEB'
        };

        // Find matching emoji and color
        let emoji = '🍽️'; // default
        let color = '#4ECDC4'; // default

        for (const key in emojiMap) {
            if (nameLower.includes(key)) {
                emoji = emojiMap[key];
                color = colorMap[key];
                break;
            }
        }

        return { emoji, color };
    };

    const fetchCategories = async () => {
        try {
            const response = await fetchData('CategoryApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                const data = response.response_data;
                if (Array.isArray(data)) {
                    // Filter out categories with "Purchase" in the name
                    const filteredData = data.filter(item => {
                        const name = item.Name || '';
                        return !name.toLowerCase().includes('purchase');
                    });

                    setCategories(filteredData.map(item => {
                        const name = item.Name;
                        const style = getCategoryStyle(name);
                        return {
                            id: item.Id,
                            name: name,
                            emoji: style.emoji,
                            color: style.color
                        };
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            // Fallback to default categories if API fails
            setCategories([
                { id: 1, name: 'Breakfast', emoji: '🍳', color: '#FF6B6B' },
                { id: 2, name: 'Lunch', emoji: '🍱', color: '#4ECDC4' },
                { id: 3, name: 'Dinner', emoji: '🍽️', color: '#95E1D3' }
            ]);
        }
    };

    const fetchCustomerOptions = async () => {
        try {
            const response = await fetchData('CustomerApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                const data = response.response_data;
                if (Array.isArray(data)) {
                    const options = data.map(item => ({
                        value: item.Id,
                        label: `${item.Name}-${item.ContactNumber1 || ''}`,
                    }));
                    setCustomerOptions(options);
                }
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomerOptions([]);
        }
    };

    const handleCategoryClick = async (category) => {
        if (!selectedCustomer) {
            setErrorMessage('Please select a customer first');
            return;
        }
        if (!fromDate || !toDate) {
            setErrorMessage('Please select date range');
            return;
        }

        setErrorMessage('');
        setActiveCategory(category.id);
        setIsLoading(true);

        try {
            // Fetch food register data
            const registerResponse = await fetchData(
                'DailyFoodRegisterApi',
                `/${companyId}/${selectedCustomer.value}/${category.id}/${fromDate}/${toDate}`,
                token
            );

            // Fetch food item options for the category
            const itemsResponse = await fetchData(
                'DailyFoodRegisterApi',
                `/${companyId}/${category.id}`,
                token
            );

            // Set food item options
            if (itemsResponse.responseCode === '1111') {
                const itemsData = itemsResponse.response_data;
                if (Array.isArray(itemsData)) {
                    const options = itemsData.map(item => ({
                        value: item.Id,
                        label: item.Name,
                        rate: item.Rate
                    }));
                    setFoodItemOptions(options);
                }
            } else {
                setFoodItemOptions([]);
            }

            // Set food register data
            if (registerResponse.responseCode === '1111') {
                const data = registerResponse.response_data;
                if (Array.isArray(data) && data.length > 0) {
                    // Map API data to food items
                    const items = data.map(item => {
                        const rate = item.Rate || 0;
                        const quantity = item.Quantity || 1;
                        return {
                            RegisterDate: item.RegisterDate,
                            ItemId: item.ItemId || '',
                            CategoryId: item.CategoryId || '',
                            CustomerId: item.CustomerId || '',
                            Id: item.Id || 0,
                            ItemName: item.ItemName || '',
                            Rate: rate,
                            Quantity: quantity,
                            TransportationCharges: item.TransportationCharges || 0,
                            Amount: rate * quantity,
                        };
                    });
                    setFoodItems(items);
                } else {
                    // No data found, don't add any rows
                    setFoodItems([]);
                }
            } else {
                // API error, don't add any rows
                setFoodItems([]);
            }
        } catch (error) {
            console.error('Error fetching food items:', error);
            // On error, don't add any rows
            setFoodItems([]);
            setFoodItemOptions([]);
        } finally {
            setIsLoading(false);
        }
    };



    const handleApplyToAllChange = (checked) => {
        setApplyToAll(checked);

        // If checkbox is checked and first row has an item selected, apply it to all rows
        if (checked && foodItems.length > 0 && foodItems[0].ItemId) {
            const firstItem = foodItems[0];
            const newItems = foodItems.map(item => ({
                ...item,
                ItemId: firstItem.ItemId,
                ItemName: firstItem.ItemName,
                Rate: firstItem.Rate,
                Amount: firstItem.Rate * item.Quantity,
            }));
            setFoodItems(newItems);
        }
    };

    const handleItemSelect = (selectedOption, index) => {
        const updatedItems = [...foodItems];

        if (!selectedOption) {
            updatedItems[index] = {
                ...updatedItems[index],
                ItemId: '',
                ItemName: '',
                Rate: 0,
                Amount: 0
            };
            setFoodItems(updatedItems);
            return;
        }

        // Always apply only to the current row
        updatedItems[index] = {
            ...updatedItems[index],
            ItemId: selectedOption.value,
            ItemName: selectedOption.label,
            Rate: selectedOption.rate || 0,
            Amount: (selectedOption.rate || 0) * updatedItems[index].Quantity
        };
        setFoodItems(updatedItems);
    };

    const handleInputChange = (index, field, value) => {
        const updatedItems = [...foodItems];
        updatedItems[index][field] = parseFloat(value) || 0;

        if (field === 'Quantity' || field === 'Rate') {
            updatedItems[index].Amount =
                updatedItems[index].Rate * updatedItems[index].Quantity;
        }

        setFoodItems(updatedItems);
    };



    const handleSubmit = async () => {
        if (!selectedCustomer) {
            setErrorMessage('Please select a customer');
            return;
        }

        const validItems = foodItems
            .filter(item => item.ItemId && item.ItemId !== '')
            .map(item => ({
                Id: item.Id || 0,
                RegisterDate: item.RegisterDate,
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                Rate: item.Rate,
                Quantity: item.Quantity,
                Amount: item.Amount,
                TransportationCharges: item.TransportationCharges || 0,
                CategoryId: activeCategory,
                CustomerId: selectedCustomer.value
            }));

        if (validItems.length === 0) {
            setErrorMessage('Please add at least one food item');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        const submitData = {
            CustomerId: selectedCustomer.value,
            CategoryId: activeCategory,
            DailyFoodRegister: validItems,
            CreatedBy: userId,
            CompanyId: companyId
        };

        try {
            const response = await postData('DailyFoodRegisterApi', submitData, token);
            if (response.responseCode === "1111") {
                setShowSuccessPopup(true);
                setTimeout(() => {
                    setShowSuccessPopup(false);
                    resetForm();
                }, 2000);
            } else {
                setErrorMessage('Failed to save food register');
            }
        } catch (error) {
            console.error('Error saving food register:', error);
            setErrorMessage('Error saving food register');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedCustomer(null);
        setActiveCategory(null);
        setFoodItems([]);
        const today = new Date().toISOString().split('T')[0];
        setFromDate(today);
        setToDate(today);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const getTotalAmount = () => {
        return foodItems.reduce((sum, item) => sum + (item.Amount || 0) + (item.TransportCharge || 0), 0);
    };

    return (
        <div className="food-register-container">
            {/* Header */}
            <div className="food-header">
                <h2 className="food-title">
                    <span className="title-icon">🍴</span>
                    Daily Food Register
                </h2>
                <div className="header-actions">
                    <Button
                        className="btn-back"
                        onClick={() => history.goBack()}
                        startIcon={<CloseIcon />}
                    >
                        Back
                    </Button>
                </div>
            </div>

            {errorMessage && (
                <div className="error-banner">
                    <span className="error-icon">⚠️</span>
                    {errorMessage}
                </div>
            )}

            {/* Customer & Date Selection + Category Buttons */}
            <div className="selection-card">
                <div className="single-line-section">
                    <div className="form-group">
                        <label>Customer Name <span className="required">*</span></label>
                        <Select
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                            options={customerOptions}
                            placeholder="Select customer..."
                            className="customer-select"
                            classNamePrefix="select"
                            isClearable
                            isSearchable={true}
                        />
                    </div>
                    <div className="form-group">
                        <label>From Date <span className="required">*</span></label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="date-input"
                        />
                    </div>
                    <div className="form-group">
                        <label>To Date <span className="required">*</span></label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="date-input"
                        />
                    </div>

                    <div className="category-buttons-wrapper">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                                style={{ '--category-color': category.color }}
                                onClick={() => handleCategoryClick(category)}
                                disabled={isLoading}
                            >
                                <span className="category-emoji">{category.emoji}</span>
                                <span className="category-name">{category.name}</span>
                                {activeCategory === category.id && (
                                    <span className="active-indicator">✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Food Items Table */}
            {activeCategory && (
                <div className="items-section">
                    <div className="items-header">
                        <h3 className="section-title">
                            {categories.find(c => c.id === activeCategory)?.emoji} {categories.find(c => c.id === activeCategory)?.name} - Food Items
                        </h3>
                        <Button
                            className="btn-save"
                            onClick={handleSubmit}
                            startIcon={<SaveAsIcon />}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save Register'}
                        </Button>
                    </div>

                    <div className="table-container">
                        <table className="food-table">
                            <thead>
                                <tr>
                                    <th className="col-date">Date</th>
                                    <th className="col-item">Item</th>
                                    <th className="col-checkbox">Same</th>
                                    <th className="col-number">Quantity</th>
                                    <th className="col-number">Rate</th>
                                    <th className="col-number">Amount</th>
                                    <th className="col-number">Transport</th>
                                </tr>
                            </thead>
                            <tbody>
                                {foodItems.map((item, index) => (
                                    <tr key={index} className="table-row">
                                        <td className="date-cell">{formatDate(item.RegisterDate)}</td>
                                        <td className="item-cell">
                                            <Select
                                                value={item.ItemId ? { value: item.ItemId, label: item.ItemName } : null}
                                                onChange={(option) => handleItemSelect(option, index)}
                                                options={foodItemOptions}
                                                placeholder="Select item..."
                                                className="item-select"
                                                classNamePrefix="select"
                                                isClearable
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        borderRadius: '10px',
                                                        borderColor: state.isFocused ? '#667eea' : '#e5e7eb',
                                                        boxShadow: state.isFocused ? '0 0 0 2px rgba(102, 126, 234, 0.1)' : 'none',
                                                        '&:hover': { borderColor: '#667eea' },
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '12.5px',
                                                    }),
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        fontSize: '12px',
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        fontSize: '12px',
                                                        padding: '6px 10px',
                                                    }),
                                                    singleValue: (base) => ({
                                                        ...base,
                                                        fontSize: '12px',
                                                    }),
                                                }}
                                            />
                                        </td>
                                        <td className="checkbox-cell">
                                            {index === 0 && (
                                                <input
                                                    type="checkbox"
                                                    checked={applyToAll}
                                                    onChange={(e) => handleApplyToAllChange(e.target.checked)}
                                                    className="apply-to-all-checkbox"
                                                    title="Apply first row item to all rows"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={item.Quantity}
                                                onChange={(e) => handleInputChange(index, 'Quantity', e.target.value)}
                                                className="table-input"
                                                min="1"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={item.Rate}
                                                onChange={(e) => handleInputChange(index, 'Rate', e.target.value)}
                                                className="table-input"
                                                step="0.01"
                                                readOnly
                                            />
                                        </td>

                                        <td>
                                            <input
                                                value={item.Amount.toFixed(2)}
                                                className="table-input readonly"
                                                readOnly
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={item.TransportationCharges}
                                                onChange={(e) => handleInputChange(index, 'TransportationCharges', e.target.value)}
                                                className="table-input"
                                                step="0.01"
                                                readOnly
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="total-row">
                                    <td colSpan="5" className="total-label">Total Amount</td>
                                    <td colSpan="2" className="total-value">
                                        ₹ {getTotalAmount().toFixed(2)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {!activeCategory && (
                <div className="empty-state">
                    <div className="empty-icon">🍽️</div>
                    <p>Select a meal category to start adding food items</p>
                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="advanced-spinner">
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-icon">🍴</div>
                        </div>
                        <p className="loading-text">Loading food items...</p>
                    </div>
                </div>
            )}

            {/* Success Popup */}
            {showSuccessPopup && (
                <div className="success-popup-overlay">
                    <div className="success-popup">
                        <div className="success-icon">✓</div>
                        <h3 className="success-title">Success!</h3>
                        <p className="success-message">
                            {categories.find(c => c.id === activeCategory)?.name} Food Register Updated
                        </p>
                    </div>
                </div>
            )}

            <div style={{
                position: 'fixed',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.3rem 0.65rem',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '14px',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '0.7rem',
                backdropFilter: 'blur(10px)'
            }}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </div>
    );
};

export default DailyFoodRegister;
